import { MapboxOverlay } from '@deck.gl/mapbox'
import chroma from 'chroma-js'
import { ArcLayer } from 'deck.gl'
import { useContext, useEffect, useMemo } from 'react'
import { useTheme } from 'styled-components'
import { FeatureCollectionWithInfoAndStyle } from '~/components/mapContent/utils/geoAndStatsToFeatureCollection'
import { alertColors } from '~/packages/general/variable/spaceTime.data'
import { truthy } from '~/prix'
import { MapContext } from './mapContext'

interface ArcProps {
  geoJson: FeatureCollectionWithInfoAndStyle
  origin?: 'previous' | 'first'
  widthMultiplier?: number
}

interface Arc {
  fromCoordinates: [number, number]
  toCoordinates: [number, number]
  width: number
  name?: string
  fromColor: [number, number, number]
  toColor: [number, number, number]
}

export default function Arc({ geoJson, origin = 'first', widthMultiplier = 4 }: ArcProps) {
  const theme = useTheme()

  const mapContext = useContext(MapContext)
  if (!mapContext) {
    throw new Error('Marker must be used within a Map')
  }

  const { map, is3D } = mapContext

  const arcs: Arc[] = useMemo(
    () =>
      geoJson.features
        .map((feature, index, array) => {
          if (index === 0) {
            return null
          }
          const from = array[origin === 'first' ? 0 : index - 1]
          const to = feature

          const fromCoordinates = from.geometry.coordinates as [number, number]
          const toCoordinates = to.geometry.coordinates as [number, number]
          const width = (to.properties.value ?? 1) * widthMultiplier
          const name = `${from.properties.name} → ${to.properties.name}`

          const fromColorString = from.properties.alert
            ? alertColors[from.properties.alert]
            : theme.colors.primary
          const fromColor = chroma(fromColorString).rgb()

          const toColorString = to.properties.alert
            ? alertColors[to.properties.alert]
            : theme.colors.secondary
          const toColor = chroma(toColorString).rgb()
          return { fromCoordinates, toCoordinates, width, name, fromColor, toColor }
        })
        .filter(truthy),
    [geoJson, origin, widthMultiplier, theme.colors.primary, theme.colors.secondary],
  )

  useEffect(() => {
    try {
      if (!map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    const layer = new ArcLayer<Arc>({
      id: 'arc-layer',
      data: arcs,
      getSourcePosition: arc => arc.fromCoordinates,
      getTargetPosition: arc => arc.toCoordinates,
      getSourceColor: arc => arc.fromColor,
      getTargetColor: arc => arc.toColor,
      getWidth: arc => arc.width,
      pickable: true,
    })

    const deckOverlay = new MapboxOverlay({
      interleaved: true,
      layers: [layer],
      getTooltip: ({ object }) =>
        object
          ? {
              html: `<div>
                 <h4>${(object as Arc).name}</h4>
               </div>`,
              style: {
                backgroundColor: '#414141',
                color: '#fff',
                padding: '0px 10px',
                borderRadius: '8px',
                margin: '0px',
                lineHeight: '1',
                maxWidth: '250px',
              },
            }
          : null,
    })

    map.addControl(deckOverlay)

    return () => {
      try {
        if (!map || !map.getStyle()) {
          return
        }
      } catch {
        return
      }
      map.removeControl(deckOverlay)
    }
  }, [map, arcs, is3D])

  return null
}
